import { useId } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IconButton } from "components/button";
import Select from "components/fields/Select";

/**
 * Table footer: "Showing X–Y of Z", rows-per-page select, prev/next buttons.
 *
 * @param {object} props
 * @param {number} props.pageIndex Zero-based page.
 * @param {number} props.pageSize
 * @param {number} props.total Total rows across all pages.
 * @param {(pageIndex: number) => void} props.onPageChange
 * @param {(pageSize: number) => void} [props.onPageSizeChange] Omit to hide the size select.
 *   Callers should reset to page 0 when the size changes.
 * @param {number[]} [props.pageSizeOptions=[10, 25, 50]]
 * @param {string} [props.className]
 */
const Pagination = ({
  pageIndex,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  className = "",
}) => {
  const selectId = useId();
  const first = total === 0 ? 0 : pageIndex * pageSize + 1;
  const last = Math.min((pageIndex + 1) * pageSize, total);
  const canPrevious = pageIndex > 0;
  const canNext = (pageIndex + 1) * pageSize < total;
  const sizes = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted ${className}`.trim()}
    >
      <p aria-live="polite">
        Showing{" "}
        <span className="font-medium text-fg">
          {first.toLocaleString()}–{last.toLocaleString()}
        </span>{" "}
        of <span className="font-medium text-fg">{total.toLocaleString()}</span>
      </p>

      <div className="flex items-center gap-4">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <label htmlFor={selectId} className="whitespace-nowrap">
              Rows per page
            </label>
            <div className="w-20">
              <Select
                id={selectId}
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1">
          <IconButton
            icon={<MdChevronLeft />}
            label="Previous page"
            size="sm"
            disabled={!canPrevious}
            onClick={() => onPageChange(pageIndex - 1)}
          />
          <IconButton
            icon={<MdChevronRight />}
            label="Next page"
            size="sm"
            disabled={!canNext}
            onClick={() => onPageChange(pageIndex + 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
